import { COLORS } from '@/constants';
import { tagCategoryId } from '@/enums';
import type { AbstractLine, IEntityTag, IScene } from '@/types';
import { BooleanFilter } from './helpers';

export const isDraftLine = (line: AbstractLine) => line.code === 'X';

/** Chip/row label for a line: "A · Title" when the line has a real title,
 *  else "<prefix> A" (the localized prefix comes from the caller). */
export const formatLineLabel = (line: { code: string; title?: string }, linePrefix: string): string =>
    line.title && line.title !== line.code ? `${line.code} · ${line.title}` : `${linePrefix} ${line.code}`;

/** Compact label for the paragraph line tag (lines mockup, #1830):
 *  «Линия A1» → «A1», «любовная линия» → «лл» (initials), single-word titles
 *  keep their first two characters, untitled lines fall back to the letter code. */
export const getLineShortCode = (line: { code: string; title?: string }): string => {
    const title = line.title?.trim();
    if (!title || title === line.code) return line.code;
    const generic = title.match(/^(?:линия|line)\s+(\S{1,4})$/i);
    if (generic?.[1]) return generic[1];
    const words = title.split(/\s+/).filter(Boolean);
    if (words.length >= 2)
        return words
            .slice(0, 2)
            .map(w => w.charAt(0))
            .join('');
    return title.slice(0, 2);
};

/** Remove bracket "[..." text from a string at the given offset.
 *  When searchLength is provided, removes exactly [+searchQuery (preserving text after).
 *  Without it, falls back to removing up to the closing "]" or end of string. */
export const removeBracketText = (text: string, bracketOffset: number, searchLength?: number): string => {
    const before = text.substring(0, bracketOffset);
    if (searchLength !== undefined) {
        const after = text.substring(bracketOffset + 1 + searchLength);
        return `${before}${after}`;
    }
    const closingBracketPos = text.indexOf(']', bracketOffset);
    const after = closingBracketPos >= 0 ? text.substring(closingBracketPos + 1) : '';
    return `${before}${after}`;
};

/** Get the next available single-letter line code (A-Z) */
export const getNextCode = (lines: { code?: string }[]): string => {
    const usedCodes = new Set(lines.map(l => l.code?.toUpperCase()));
    for (let i = 0; i < 26; i++) {
        const letter = String.fromCharCode(65 + i);
        if (!usedCodes.has(letter)) return letter;
    }
    return 'A';
};

/** The first line colour no line uses yet (never the draft line's `colorX`) */
export const getNextColor = (usedColors: string[]): string => {
    const taken = new Set(usedColors);
    return COLORS.find(c => c.code !== 'colorX' && !taken.has(c.code))?.code ?? 'colorA';
};
export const isDraftLineTag = (tag: IEntityTag) => tag.name.slice(-1) === 'X' && tag.tag_category_id === tagCategoryId.lineTag;

export const getTagsWhichAreNotLines = (scene: IScene) => scene.tags.filter(tag => tag.tag_category_id !== tagCategoryId.lineTag);
export const getTagIdsWhichAreNotLines = (scene: IScene) => getTagsWhichAreNotLines(scene).map((tag: IEntityTag) => tag.id);

export const getTagIdsWhenSwitchLineForNone = (scene: IScene, line: AbstractLine) => {
    return [
        // new line now is primary
        line.entity_tag.id,
        // keep the other tags
        ...getTagIdsWhichAreNotLines(scene),
    ];
};

export const getTagIdsWhenSwitchLineForOne = (scene: IScene, line: AbstractLine) => {
    return [
        // new line now is primary
        line.entity_tag.id,
        // keep the other tags
        ...getTagIdsWhichAreNotLines(scene),
    ];
};

export const getTagIdsWhenSwitchLinesForMany = (scene: IScene, startLine: AbstractLine, endLine: AbstractLine) => {
    // for now, the first line is main
    const sceneLineTags = scene.tags.filter(tag => tag.tag_category_id === tagCategoryId.lineTag && !isDraftLineTag(tag));

    return [
        // keep the other line tags and remove startLine
        ...sceneLineTags.filter((tag: IEntityTag) => tag.id !== startLine.entity_tag.id).map((tag: IEntityTag) => tag.id),
        // new line
        endLine.entity_tag.id,
        // keep the other tags
        ...getTagIdsWhichAreNotLines(scene),
    ];
};

export const getTagIdsWhenSwitchLines = (scene: IScene, startLine: AbstractLine, endLine: AbstractLine) => {
    // cases to validate
    // Legend: X - draft line, ">" - move to, <number> - new line, T<number> - not line tag
    // 1. [X, T1, T2] X > 1 = [1, T1, T2]
    // 2. [1, T1, T2] 1 > 1 = [1, T1, T2] Should not be possible functionally
    // 3. [2, T1, T2] 2 > 1 = [1, T1, T2]
    // 4. [3, T1, T2] 3 > 1 = [1, T1, T2]
    // 5. [3, T1, T2] 3 > X = [X, T1, T2]
    // 6. [1, X] 1 > 1 = [1] Should be impossible both model-wise and functionally
    // 7. [1, 2] 2 > 1 = [1]
    // 8. [1, 2] 1 > 2 = [2]
    // 9. [1, 2] 1/2 > X = [X]
    // 10. [1, 2, X] 2 > 1 = [2]
    // 11. [1, 2, 3] 1 > 1 = [1, 2, 3] Should not be possible functionally
    // 12. [1, 2, 3] 1 > 2 = [2, 3]
    // 13. [1, 2, 3] 1 > 3 = [2, 3]
    // 14. [1, 2, 3] 1 > 4 = [2, 3, 4]
    // 15. [1, 2, 3] > X = [X]
    // 16. [1, 2, X] 1 > 3 = [2, 3] Should be impossible both model-wise

    // new line or new ?
    if (isDraftLine(endLine)) {
        // cases 5, 9, 15
        // draft line (at the moment of implementation "Line X")
        // remove all the current lines, add this new line
        return getTagIdsWhenSwitchLineForNone(scene, endLine);
    }

    // if we move a scene to not a draft line, we remove it ALWAYS
    // automatically covers cases 6, 10, 16
    const sceneLineTags = scene.tags.filter(tag => tag.tag_category_id === tagCategoryId.lineTag && !isDraftLineTag(tag));
    if (!sceneLineTags.length) {
        // somehow no sceneLineTags
        // it's alright, we just add them
        return getTagIdsWhenSwitchLineForNone(scene, endLine);
    }

    if (sceneLineTags.length === 1) {
        // is there only one line
        // cases 1, 2, 3, 4
        return getTagIdsWhenSwitchLineForOne(scene, endLine);
    }

    // cases 7, 8, 11, 12, 13, 14, 15
    return getTagIdsWhenSwitchLinesForMany(scene, startLine, endLine);
};

export const getTagsWhenSwitchLines = (
    scene: IScene,
    startLine: AbstractLine,
    endLine: AbstractLine,
    lines: AbstractLine[],
): IScene['tags'] => {
    const tagIds = getTagIdsWhenSwitchLines(scene, startLine, endLine);

    return tagIds
        .map((tagId, index) => {
            const existingTag = scene.tags.find(tag => tag.id === tagId);
            if (existingTag) {
                return existingTag;
            }
            const line = lines.find(line => line.entity_tag.id === tagId);
            if (line) {
                // id is temporary
                return {
                    id: index,
                    name: '',
                    tag_category_id: tagCategoryId.lineTag,
                    color: line.color,
                };
            }

            return null;
        })
        .filter(BooleanFilter);
};
